
'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from 'next-themes';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Eye, EyeOff, Smartphone, Sparkles, Workflow, FileCog, Headset, Star, Loader2, Bot } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { signUp } from '@/services/auth';
import ErrorToastHandler from '@/components/dashboard/error-toast';

const GoogleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg role="img" viewBox="0 0 24 24" {...props}>
        <path
            d="M12.48 10.92v3.28h7.84c-.24 1.84-.854 3.187-1.787 4.133-1.147 1.147-2.933 2.4-5.113 2.4-4.333 0-7.867-3.587-7.867-8s3.534-8 7.867-8c2.333 0 4.133 1 4.92 1.867l2.6-2.6C18.067 1.933 15.533 1 12.48 1 5.867 1 1 6.38 1 13s4.867 12 11.48 12c3.533 0 6.267-1.2 8.333-3.333 2.133-2.133 2.8-5.067 2.8-7.933 0-.733-.067-1.467-.2-2.133H12.48z"
            fill="currentColor"
        />
    </svg>
);

const features = [
    {
        icon: Smartphone,
        title: 'Voice AI Agent',
        description: "AgentReply's Voice AI Agent engages customers in natural conversations 24/7, handling inquiries, making calls, and providing intelligent support with seamless CRM integration."
    },
    {
        icon: Sparkles,
        title: 'Custom AI Knowledgebase',
        description: "AgentReply's AI leverages a custom knowledge base from PDFs, videos, and help articles to provide accurate, context-aware responses instantly."
    },
    {
        icon: Workflow,
        title: 'Customize Workflow Automation',
        description: 'AgentReply enables customizable workflow automation, allowing businesses to streamline tasks, trigger actions, and enhance customer interactions effortlessly.'
    },
    {
        icon: FileCog,
        title: 'Customize AI Agent according to Business Needs',
        description: 'AgentReply lets you customize your AI Agent to match your business needs, from tone and responses to workflow automation and CRM integration.'
    },
    {
        icon: Headset,
        title: '24/7 support from human like AI Agent',
        description: "AgentReply's human-like AI Agent provides 24/7 support, ensuring seamless customer interactions anytime, anywhere."
    },
    {
        icon: Star,
        title: 'Crm Integrations',
        description: 'AgentReply seamlessly integrates with CRMs like HubSpot, Shopify, Pipedrive, and webhooks, enabling smooth data sync and automated workflows.'
    }
]

const formSchema = z.object({
    firstName: z.string().min(1, { message: 'First name is required.' }),
    lastName: z.string().min(1, { message: 'Last name is required.' }),
    email: z.string().email({ message: 'Please enter a valid email address.' }),
    password: z.string().min(8, { message: 'Password must be at least 8 characters.' }),
    retypePassword: z.string(),
}).refine((data) => data.password === data.retypePassword, {
    message: "Passwords don't match",
    path: ["retypePassword"],
});

export default function SignUpPage() {
    const { setTheme } = useTheme();
    const router = useRouter();
    const { toast } = useToast();

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [retypePasswordVisible, setRetypePasswordVisible] = useState(false);

    const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
    const toggleRetypePasswordVisibility = () => setRetypePasswordVisible(!retypePasswordVisible);

    useEffect(() => {
        setTheme('light');
    }, [setTheme]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            retypePassword: '',
        },
    });

    const mutation = useMutation({
        mutationFn: signUp,
        onSuccess: () => {
            toast({
                title: 'Signup Successful',
                description: "We've sent a verification code to your email.",
            });
            router.push('/signup/verify');
        },
        onError: (error: Error) => {
            toast({
                variant: 'destructive',
                title: 'Signup Failed',
                description: error.message || 'An unexpected error occurred.',
            });
        },
    });

    const handleSignUp = (values: z.infer<typeof formSchema>) => {
        mutation.mutate(values);
    };

    const handleGoogleSignUp = () => {
        window.location.href = process.env.NEXT_PUBLIC_GOOGLE_SIGNUP_URL || '';
    };


    return (
        <>
            <ErrorToastHandler />
            <div className="flex min-h-screen w-full flex-col static-gradient-bg">
                <header className="fixed top-0 left-0 right-0 z-10 flex h-16 items-center border-b bg-background/80 px-4 shadow-sm backdrop-blur-sm sm:px-6">
                    <a href="https://agentreply.ai" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
                        <Image src="/ar_icon.png" alt="Logo" width={32} height={32} />
                        <h1 className="text-xl font-semibold text-foreground">AgentReply</h1>
                    </a>
                </header>
                <main className="flex-1 flex flex-col px-4 pt-20 pb-8 sm:px-8 sm:pb-12 lg:pt-20 lg:pb-8 xl:pt-20 xl:pb-12">
                    <div className="w-full max-w-6xl mx-auto grid grid-cols-1 items-center gap-12 lg:gap-12 xl:gap-16 lg:grid-cols-2">
                        <Card className="w-full max-w-md justify-self-center lg:justify-self-end rounded-xl shadow-2xl">
                            <CardHeader>
                                <CardTitle className="text-3xl font-bold">Sign up</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Form {...form}>
                                    <form onSubmit={form.handleSubmit(handleSignUp)} className="space-y-6">
                                        <div className="grid grid-cols-2 gap-4">
                                            <FormField
                                                control={form.control}
                                                name="firstName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>First Name</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="Enter your first name" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="lastName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Last Name</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="Enter your last name" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <FormField
                                            control={form.control}
                                            name="email"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Email</FormLabel>
                                                    <FormControl>
                                                        <Input type="email" placeholder="Enter your email address" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="password"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Password</FormLabel>
                                                    <div className="relative">
                                                        <FormControl>
                                                            <Input
                                                                type={passwordVisible ? "text" : "password"}
                                                                placeholder="••••••"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground"
                                                            onClick={togglePasswordVisibility}
                                                        >
                                                            {passwordVisible ? <EyeOff /> : <Eye />}
                                                        </Button>
                                                    </div>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="retypePassword"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Retype Password</FormLabel>
                                                    <div className="relative">
                                                        <FormControl>
                                                            <Input
                                                                type={retypePasswordVisible ? "text" : "password"}
                                                                placeholder="••••••"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground"
                                                            onClick={toggleRetypePasswordVisibility}
                                                        >
                                                            {retypePasswordVisible ? <EyeOff /> : <Eye />}
                                                        </Button>
                                                    </div>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <Button className="w-full text-lg h-11" type="submit" disabled={mutation.isPending}>
                                            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Sign up
                                        </Button>
                                    </form>
                                </Form>
                                <div className="flex items-center space-x-2 my-6">
                                    <Separator className="flex-1" />
                                    <span className="text-xs text-muted-foreground">or</span>
                                    <Separator className="flex-1" />
                                </div>
                                <Button variant="outline" onClick={handleGoogleSignUp} className="w-full text-base h-11">
                                    <GoogleIcon className="mr-2 h-5 w-5" />
                                    Sign up with Google
                                </Button>
                                <div className="text-center text-sm mt-6">
                                    Already have an account?{' '}
                                    <Link href="/signin" className="font-semibold text-primary hover:underline">
                                        Login
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="flex flex-col space-y-5 lg:space-y-4 xl:space-y-8 w-full max-w-md lg:max-w-none mx-auto lg:mx-0 mt-8 lg:mt-0 px-2 sm:px-0">
                            {features.map((feature, index) => (
                                <div key={index} className="flex items-start gap-3 sm:gap-4 lg:gap-3 xl:gap-4 w-full overflow-hidden">
                                    <div className="flex h-8 w-8 sm:h-10 sm:w-10 lg:h-8 lg:w-8 xl:h-10 xl:w-10 flex-shrink-0 items-center justify-center rounded-full bg-accent sm:mt-0.5 lg:mt-0 xl:mt-0.5">
                                        <feature.icon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-4 lg:w-4 xl:h-5 xl:w-5 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-base sm:text-lg lg:text-base xl:text-lg font-semibold text-wrap break-words leading-tight">{feature.title}</h3>
                                        <p className="mt-1 lg:mt-0.5 xl:mt-1.5 text-sm sm:text-base lg:text-sm xl:text-base text-muted-foreground text-wrap break-words leading-snug">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}
